/**
 * StickyChatWindow — LinkedIn-style chat panel (desktop only)
 *
 * Full-featured version of ChatPage for the sticky chat widget.
 * 340px fixed panel with header, messages, and full input
 * (emoji picker, file upload, voice recording).
 * Connects via WebSocket, supports optimistic sends and mark-as-read.
 */

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  memo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { useAuthStore } from '@/features/auth';
import {
  useConversation,
  useMessages,
  useAddMessageToCache,
  useUpdateMessageInCache,
  useMarkConversationReadInCache,
  useMarkMessagesReadInCache,
  getConversationDisplayName,
  getConversationAvatar,
} from '../hooks';
import { useChat } from '../hooks';
import { useOptimisticMessages } from '../hooks';
import { usePresence } from '../hooks';
import { useTypingStore, useConversationTyping } from '../hooks';
import { useUpload } from '../hooks';
import { ChatAvatar } from './ChatAvatar';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';
import { ConnectionStatusBanner } from './ConnectionStatusBanner';
import { VoiceRecorder } from './VoiceRecorder';
import { EmojiPicker } from './EmojiPicker';
import { MediaPicker } from './MediaPicker';
import { ConversationActionMenu } from './ConversationActionMenu';
import { useChatWidgetStore } from '../store';
import { getMessagingFlow, setPendingPush } from '../messagingNavBridge';
import {
  useMarkConversationUnread,
  useMuteConversation,
  useArchiveConversation,
  useLeaveConversation,
  useDeleteConversation,
} from '../hooks';
import type { VoiceRecorderResult } from '../hooks/useVoiceRecorder';
import type {
  ChatMessage,
  WsMessageEvent,
  WsTypingEvent,
  WsReactionEvent,
  WsDeleteEvent,
  WsEditEvent,
  WsReadEvent,
  WsPresenceEvent,
} from '../types';
import { generateVideoThumbnail } from '../utils/generateVideoThumbnail';
import { isSameDay } from '../utils/dateUtils';
import { DateSeparator } from './DateSeparator';
import { MediaViewer } from './MediaViewer';
import { useMediaGallery } from '../hooks/useMediaGallery';

// =============================================================================
// Message Input (full — emoji, attachment, voice)
// =============================================================================

interface WidgetMessageInputProps {
  onSend: (content: string) => void;
  onTyping: (isTyping: boolean) => void;
  disabled?: boolean;
  onMicPress?: () => void;
  onAttachPress?: () => void;
}

const WidgetMessageInput = memo(function WidgetMessageInput({
  onSend,
  onTyping,
  disabled,
  onMicPress,
  onAttachPress,
}: WidgetMessageInputProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Auto-focus textarea when connection is ready (disabled becomes false)
  useEffect(() => {
    if (!disabled) {
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [disabled]);

  const handleEmojiInsert = useCallback((emoji: string) => {
    setText((prev) => prev + emoji);
    setShowEmojiPicker(false);
    textareaRef.current?.focus();
  }, []);

  const handleInput = useCallback(
    (value: string) => {
      setText(value);

      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
      }

      if (!isTypingRef.current && value.trim()) {
        isTypingRef.current = true;
        onTyping(true);
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        isTypingRef.current = false;
        onTyping(false);
      }, 2000);
    },
    [onTyping],
  );

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setText('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    if (isTypingRef.current) {
      isTypingRef.current = false;
      onTyping(false);
    }
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
  }, [text, onSend, onTyping]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  return (
    <div className="flex-shrink-0 border-t px-3 py-2" style={{ borderColor: 'var(--user-page-card-border)' }}>
      <div className="flex items-end gap-1.5">
        {/* Attachment button */}
        {onAttachPress && (
          <button
            type="button"
            onClick={onAttachPress}
            disabled={disabled}
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border transition-all hover-card active:scale-95 disabled:opacity-40"
            style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)', color: 'var(--user-page-text)' }}
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        )}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => handleInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('messaging.typeMessage')}
          disabled={disabled}
          rows={3}
          dir="auto"
          className="flex-1 resize-none rounded-lg border px-2.5 py-1.5 text-xs placeholder:[color:var(--user-page-text)] focus:outline-none disabled:opacity-50"
          style={{ maxHeight: 120, borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)', color: 'var(--user-page-heading)' }}
        />
        {/* Emoji button */}
        <button
          type="button"
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          disabled={disabled}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border transition-all hover-card active:scale-95 disabled:opacity-40"
          style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)', color: 'var(--user-page-text)' }}
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M8 14s1.5 2 4 2 4-2 4-2" />
            <line x1="9" y1="9" x2="9.01" y2="9" />
            <line x1="15" y1="9" x2="15.01" y2="9" />
          </svg>
        </button>
        {/* Mic button */}
        <button
          type="button"
          onPointerDown={onMicPress}
          disabled={disabled}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border transition-all hover-card active:scale-95 disabled:opacity-40"
          style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)', color: 'var(--user-page-text)' }}
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
            <path d="M19 10v2a7 7 0 01-14 0v-2" />
            <line x1="12" y1="19" x2="12" y2="23" />
            <line x1="8" y1="23" x2="16" y2="23" />
          </svg>
        </button>
        {/* Send button — always visible, dimmed when no text */}
        <button
          type="button"
          onClick={handleSend}
          disabled={disabled || !text.trim()}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-all disabled:opacity-40"
          style={{ backgroundColor: text.trim() ? 'var(--user-page-heading)' : 'var(--user-page-card-bg)', color: text.trim() ? '#fff' : 'var(--user-page-card-border)' }}
        >
          <svg className="h-3.5 w-3.5 -rotate-45 rtl:rotate-45" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
      {/* Inline Emoji Picker */}
      <EmojiPicker
        isOpen={showEmojiPicker}
        onClose={() => setShowEmojiPicker(false)}
        onEmojiSelect={handleEmojiInsert}
        bottomOffset={0}
      />
    </div>
  );
});

// =============================================================================
// Main Component
// =============================================================================

export function StickyChatWindow() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const currentUserId = useAuthStore((s) => s.user?.id) || '';

  const { activeConversationId, isChatMinimized, minimizeChat, restoreChat, closeChat } =
    useChatWidgetStore();
  const [headerMenuPos, setHeaderMenuPos] = useState<{ x: number; y: number } | null>(null);

  const conversationId = activeConversationId!;

  // State
  const typingUsers = useConversationTyping(conversationId);
  const { setTyping, clearTyping } = useTypingStore();
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Conversation action hooks
  const markUnread = useMarkConversationUnread();
  const muteConversation = useMuteConversation();
  const archiveConversation = useArchiveConversation();
  const leaveConversation = useLeaveConversation();
  const deleteConversation = useDeleteConversation();

  // Data queries
  const { data: conversation } = useConversation(conversationId);
  const {
    data: messagesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMessages(conversationId);

  const serverMessages = useMemo(() => {
    if (!messagesData?.pages) return [];
    return messagesData.pages.flatMap((page) => page.messages);
  }, [messagesData]);

  const addMessageToCache = useAddMessageToCache();
  const updateMessageInCache = useUpdateMessageInCache();
  const markConversationRead = useMarkConversationReadInCache();
  const markMessagesRead = useMarkMessagesReadInCache();

  // Optimistic messages (no REST send — WebSocket sends directly)
  const sendMessage = useCallback(
    async (_params: { content: string; type?: string; replyToId?: string }) => {
      // no-op: actual send happens via WebSocket sendText
    },
    [],
  );

  const { optimisticMessages, sendOptimistic, retrySend, removeOptimistic } =
    useOptimisticMessages({
      messages: serverMessages,
      conversationId,
      sendMessage,
    });

  // Media gallery for lightbox viewer
  const { items: mediaGallery, findIndex: findMediaIndex } = useMediaGallery(optimisticMessages);
  const [viewerIndex, setViewerIndex] = useState(-1);

  const handleMediaTap = useCallback((messageId: string) => {
    const idx = findMediaIndex(messageId);
    if (idx >= 0) setViewerIndex(idx);
  }, [findMediaIndex]);

  // Presence (DM partner)
  const dmPartnerId = useMemo(() => {
    if (!conversation || conversation.type !== 'dm') return null;
    const partner = conversation.participants.find((p) => p.userId !== currentUserId);
    return partner?.userId || null;
  }, [conversation, currentUserId]);

  const { isOnline, lastSeenFormatted, updateFromWebSocket: updatePresence } = usePresence(dmPartnerId);

  const displayName = conversation
    ? getConversationDisplayName(conversation, currentUserId)
    : '';
  const displayAvatar = conversation
    ? getConversationAvatar(conversation, currentUserId)
    : null;

  // =========================================================================
  // WebSocket handlers
  // =========================================================================

  const handleWsMessage = useCallback(
    (event: WsMessageEvent) => {
      const newMessage: ChatMessage = {
        id: event.id,
        conversationId,
        senderId: event.senderId,
        sequenceId: event.sequenceId,
        type: event.messageType,
        content: event.content || null,
        mediaUrl: event.mediaUrl || null,
        mediaThumbnail: event.mediaThumbnail || null,
        mediaMetadata: (event.mediaMetadata as ChatMessage['mediaMetadata']) || null,
        replyTo: event.replyTo || null,
        isEdited: false,
        editedAt: null,
        isDeleted: false,
        createdAt: new Date(event.timestamp).toISOString(),
        sender: {
          id: event.senderId,
          name: event.senderName,
          avatar: event.senderAvatar,
        },
        reactions: {},
        isStarred: false,
      };
      addMessageToCache(conversationId, newMessage);
    },
    [conversationId, addMessageToCache],
  );

  const handleWsTyping = useCallback(
    (event: WsTypingEvent) => {
      if (event.userId === currentUserId) return;
      if (event.isTyping) {
        setTyping(conversationId, { userId: event.userId, userName: event.userName, userAvatar: event.userAvatar });
      } else {
        clearTyping(conversationId, event.userId);
      }
    },
    [currentUserId, conversationId, setTyping, clearTyping],
  );

  const handleWsReaction = useCallback(
    (event: WsReactionEvent) => {
      updateMessageInCache(conversationId, event.messageId, (msg) => {
        const reactions = { ...msg.reactions };
        const users = reactions[event.emoji] ? [...reactions[event.emoji]] : [];
        if (event.action === 'add' && !users.includes(event.userId)) {
          users.push(event.userId);
        } else if (event.action === 'remove') {
          const idx = users.indexOf(event.userId);
          if (idx >= 0) users.splice(idx, 1);
        }
        reactions[event.emoji] = users;
        if (users.length === 0) delete reactions[event.emoji];
        return { ...msg, reactions };
      });
    },
    [conversationId, updateMessageInCache],
  );

  const handleWsDelete = useCallback(
    (event: WsDeleteEvent) => {
      updateMessageInCache(conversationId, event.messageId, (msg) => ({
        ...msg,
        isDeleted: true,
        content: null,
      }));
    },
    [conversationId, updateMessageInCache],
  );

  const handleWsEdit = useCallback(
    (event: WsEditEvent) => {
      updateMessageInCache(conversationId, event.messageId, (msg) => ({
        ...msg,
        content: event.content,
        isEdited: true,
        editedAt: new Date(event.editedAt).toISOString(),
      }));
    },
    [conversationId, updateMessageInCache],
  );

  const handleWsPresence = useCallback(
    (event: WsPresenceEvent) => {
      if (event.userId === dmPartnerId) {
        updatePresence({ status: event.status, lastSeen: event.lastSeen });
      }
    },
    [dmPartnerId, updatePresence],
  );

  const handleWsRead = useCallback(
    (event: WsReadEvent) => {
      const userId = useAuthStore.getState().user?.id;
      if (userId) {
        markMessagesRead(conversationId, event.upToSequenceId, userId);
      }
    },
    [conversationId, markMessagesRead],
  );

  // Connect WebSocket
  const { isConnected, sendText, sendMedia, sendTyping, sendRead } = useChat({
    conversationId,
    enabled: true,
    onMessage: handleWsMessage,
    onTyping: handleWsTyping,
    onRead: handleWsRead,
    onReaction: handleWsReaction,
    onDelete: handleWsDelete,
    onEdit: handleWsEdit,
    onPresence: handleWsPresence,
  });

  // Upload + media/voice state
  const { upload: uploadFile } = useUpload();
  const [isRecordingVoice, setIsRecordingVoice] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);

  // =========================================================================
  // Scroll management
  // =========================================================================

  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({
      behavior: smooth ? 'smooth' : 'instant',
    });
  }, []);

  useEffect(() => {
    scrollToBottom(true);
  }, [optimisticMessages.length, scrollToBottom]);

  useEffect(() => {
    scrollToBottom(false);
  }, [conversationId, scrollToBottom]);

  // Mark messages as read
  useEffect(() => {
    if (!isConnected || serverMessages.length === 0) return;
    const lastMsg = serverMessages[serverMessages.length - 1];
    if (lastMsg && lastMsg.sequenceId > 0) {
      sendRead(lastMsg.sequenceId);
      // Optimistically clear unread badge — don't wait for server refetch
      markConversationRead(conversationId, lastMsg.sequenceId);
    }
  }, [isConnected, serverMessages, sendRead, conversationId, markConversationRead]);

  // Load more on scroll to top + track scroll-to-bottom button
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (container.scrollTop < 100 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollButton(distanceFromBottom > 200);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // =========================================================================
  // Message handlers
  // =========================================================================

  const handleSendMessage = useCallback(
    (content: string) => {
      sendText(content);
      sendOptimistic({ content });
    },
    [sendText, sendOptimistic],
  );

  // Voice recording handler
  const handleVoiceRecordingComplete = useCallback(
    async (result: VoiceRecorderResult) => {
      setIsRecordingVoice(false);

      const ext = result.mimeType.includes('webm') ? 'webm' : result.mimeType.includes('mp4') ? 'mp4' : 'ogg';
      const file = new File([result.blob], `voice-${Date.now()}.${ext}`, { type: result.mimeType });
      const uploadResult = await uploadFile(file, 'message');

      if (uploadResult) {
        sendMedia('audio', uploadResult.url, undefined, {
          duration: result.duration,
          mimeType: result.mimeType,
          fileSize: result.blob.size,
          fileName: file.name,
          waveformPeaks: result.waveformPeaks,
        });
      }
    },
    [uploadFile, sendMedia],
  );

  // Media upload handler
  const handleMediaFiles = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        const uploadResult = await uploadFile(file, 'message');
        if (uploadResult) {
          const mediaType = file.type.startsWith('image/')
            ? 'image' as const
            : file.type.startsWith('video/')
            ? 'video' as const
            : 'file' as const;

          let mediaThumbnail: string | undefined;
          if (mediaType === 'video') {
            const thumbBlob = await generateVideoThumbnail(file);
            if (thumbBlob) {
              const thumbFile = new File([thumbBlob], `thumb-${file.name}.webp`, { type: 'image/webp' });
              const thumbResult = await uploadFile(thumbFile, 'message');
              mediaThumbnail = thumbResult?.url;
            }
          }

          sendMedia(mediaType, uploadResult.url, mediaThumbnail, {
            mimeType: file.type,
            fileSize: file.size,
            fileName: file.name,
          });
        }
      }
    },
    [uploadFile, sendMedia],
  );

  // Expand to full page
  const handleExpand = useCallback(() => {
    const flow = getMessagingFlow();
    if (flow) {
      // Already on messages page — push directly via Stackflow
      flow.push('ChatActivity', { conversationId });
    } else {
      // Not on messages page — queue push for when the messaging stack mounts
      setPendingPush('ChatActivity', { conversationId });
      navigate(`/messages/${conversationId}`);
    }
    closeChat();
  }, [navigate, conversationId, closeChat]);

  // =========================================================================
  // Conversation actions (header menu)
  // =========================================================================

  const handleMarkUnread = useCallback(() => {
    markUnread(conversationId);
    closeChat();
  }, [markUnread, conversationId, closeChat]);

  const handleToggleMute = useCallback(() => {
    muteConversation.mutate({
      conversationId,
      isMuted: !conversation?.isMuted,
    });
  }, [muteConversation, conversationId, conversation?.isMuted]);

  const handleToggleArchive = useCallback(() => {
    archiveConversation.mutate({
      conversationId,
      isArchived: !conversation?.isArchived,
    });
    closeChat();
  }, [archiveConversation, conversationId, conversation?.isArchived, closeChat]);

  const handleLeave = useCallback(() => {
    if (window.confirm(t('messaging.leaveConfirm'))) {
      leaveConversation.mutate(conversationId);
      closeChat();
    }
  }, [leaveConversation, conversationId, closeChat, t]);

  const handleDelete = useCallback(() => {
    if (window.confirm(t('messaging.deleteConversationConfirm'))) {
      deleteConversation.mutate(conversationId);
      closeChat();
    }
  }, [deleteConversation, conversationId, closeChat, t]);

  // =========================================================================
  // Message grouping
  // =========================================================================

  const groupedMessages = useMemo(() => {
    return optimisticMessages.map((msg, i) => {
      const prev = i > 0 ? optimisticMessages[i - 1] : null;
      const sameSender = prev?.senderId === msg.senderId;
      const withinTime =
        prev &&
        Math.abs(
          new Date(msg.createdAt).getTime() - new Date(prev.createdAt).getTime(),
        ) < 5 * 60 * 1000;
      const isGrouped = sameSender && withinTime;
      const showSender = !sameSender && msg.type !== 'system';
      const showAvatar = showSender;
      return { msg, isGrouped: !!isGrouped, showSender, showAvatar };
    });
  }, [optimisticMessages]);

  // Mini read-receipt avatar — find last own message that peer has read (DM only)
  const lastReadMessageId = useMemo(() => {
    if (!conversation || conversation.type !== 'dm') return null;
    // Walk backwards to find the last own message marked as read
    for (let i = optimisticMessages.length - 1; i >= 0; i--) {
      const msg = optimisticMessages[i];
      if (msg.senderId === currentUserId && msg.isRead) {
        return msg.id;
      }
    }
    return null;
  }, [optimisticMessages, currentUserId, conversation]);

  // =========================================================================
  // Render
  // =========================================================================

  return (
    <motion.div
      className="flex w-[440px] flex-col overflow-hidden rounded-t-xl border shadow-lg"
      style={{ borderColor: 'var(--user-page-card-border)', backgroundColor: 'var(--user-page-card-bg)' }}
      animate={{ height: isChatMinimized ? 48 : 'calc(100dvh - 16px)' }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
    >
      {/* Header */}
      <div className="flex h-12 flex-shrink-0 items-center gap-2 border-b px-3" style={{ borderColor: 'var(--user-page-card-border)' }}>
        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <ChatAvatar src={displayAvatar} name={displayName} size="h-7 w-7" />
          {isOnline && (
            <div className="absolute -bottom-0.5 -end-0.5 h-2 w-2 rounded-full border bg-success-400" style={{ borderColor: 'var(--user-page-card-bg)' }} />
          )}
        </div>

        {/* Name + status — clickable to toggle minimize */}
        <button
          type="button"
          onClick={isChatMinimized ? restoreChat : minimizeChat}
          className="flex flex-1 flex-col overflow-hidden text-start"
        >
          <span className="truncate text-xs font-semibold" style={{ color: 'var(--user-page-heading)' }}>{displayName}</span>
          {isOnline ? (
            <span className="text-[10px] font-medium text-success-600">
              {t('messaging.activeNow')}
            </span>
          ) : lastSeenFormatted ? (
            <span className="text-[10px] font-medium" style={{ color: 'var(--user-page-text)' }}>
              {t('messaging.activeAgo', { time: lastSeenFormatted })}
            </span>
          ) : null}
        </button>

        {/* Connection indicator */}
        {!isConnected && (
          <div className="h-2 w-2 animate-pulse rounded-full bg-warning-400" title={t('messaging.connecting')} />
        )}

        {/* Header action menu (···) */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            const rect = (e.target as HTMLElement).getBoundingClientRect();
            setHeaderMenuPos({ x: rect.left, y: rect.bottom + 4 });
          }}
          className="flex h-6 w-6 items-center justify-center rounded-full transition-colors hover-card"
          style={{ color: 'var(--user-page-text)' }}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
        </button>

        {/* Minimize */}
        <button
          type="button"
          onClick={isChatMinimized ? restoreChat : minimizeChat}
          className="flex h-6 w-6 items-center justify-center rounded-full transition-colors hover-card"
          style={{ color: 'var(--user-page-text)' }}
        >
          <svg
            className={`h-3.5 w-3.5 transition-transform ${isChatMinimized ? '' : 'rotate-180'}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>

        {/* Expand to full page */}
        <button
          type="button"
          onClick={handleExpand}
          className="flex h-6 w-6 items-center justify-center rounded-full transition-colors hover-card"
          style={{ color: 'var(--user-page-text)' }}
          title={t('messaging.widget.expand')}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </button>

        {/* Close */}
        <button
          type="button"
          onClick={closeChat}
          className="flex h-6 w-6 items-center justify-center rounded-full transition-colors hover-card"
          style={{ color: 'var(--user-page-text)' }}
        >
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Body — hidden when minimized via overflow:hidden + height animation */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Connection status banner */}
        <ConnectionStatusBanner isConnected={isConnected} />

        {/* Messages */}
        <div
          ref={messagesContainerRef}
          className="relative flex-1 overflow-y-auto px-1 py-2"
          onScroll={handleScroll}
        >
          {isFetchingNextPage && (
            <div className="flex justify-center py-2">
              <div className="h-4 w-4 animate-spin rounded-full border" style={{ borderColor: 'var(--user-page-card-border)', borderTopColor: 'var(--user-page-heading)' }} />
            </div>
          )}

          {groupedMessages.map(({ msg, isGrouped, showSender, showAvatar }, i) => {
            const prevMsg = i > 0 ? groupedMessages[i - 1].msg : null;
            const showDateSeparator = !prevMsg || !isSameDay(
              new Date(msg.createdAt),
              new Date(prevMsg.createdAt),
            );

            const isLastReadMessage = msg.id === lastReadMessageId;

            return (
              <div key={msg.id}>
                {showDateSeparator && <DateSeparator date={new Date(msg.createdAt)} />}
                <MessageBubble
                  message={msg}
                  showSender={showSender}
                  showAvatar={showAvatar}
                  isGrouped={isGrouped && !showDateSeparator}
                  onRetry={retrySend}
                  onRemoveOptimistic={removeOptimistic}
                  onMediaTap={handleMediaTap}
                />
                {/* Mini read-receipt avatar */}
                {isLastReadMessage && displayAvatar && (
                  <div className="flex justify-end pe-2 -mt-0.5 mb-1">
                    <ChatAvatar src={displayAvatar} name={displayName} size="h-3.5 w-3.5" />
                  </div>
                )}
              </div>
            );
          })}

          <TypingIndicator typingUsers={typingUsers} />
          <div ref={messagesEndRef} />

          {/* Scroll-to-bottom button */}
          <AnimatePresence>
            {showScrollButton && (
              <motion.button
                type="button"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                onClick={() => scrollToBottom(true)}
                className="sticky bottom-2 start-[calc(100%-44px)] z-10 flex h-8 w-8 items-center justify-center rounded-full shadow-lg transition-colors"
                style={{ backgroundColor: 'var(--user-page-heading)', color: '#fff' }}
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* Input — swap for VoiceRecorder when recording */}
        <AnimatePresence mode="wait">
          {isRecordingVoice ? (
            <VoiceRecorder
              key="voice-recorder"
              onRecordingComplete={handleVoiceRecordingComplete}
              onCancel={() => setIsRecordingVoice(false)}
            />
          ) : (
            <WidgetMessageInput
              key="message-input"
              onSend={handleSendMessage}
              onTyping={sendTyping}
              disabled={!isConnected}
              onMicPress={() => setIsRecordingVoice(true)}
              onAttachPress={() => setIsMediaPickerOpen(true)}
            />
          )}
        </AnimatePresence>

        {/* Media Picker */}
        <MediaPicker
          isOpen={isMediaPickerOpen}
          onClose={() => setIsMediaPickerOpen(false)}
          onFilesSelected={handleMediaFiles}
        />
      </div>

      {/* Media Viewer Lightbox */}
      <MediaViewer
        items={mediaGallery}
        initialIndex={viewerIndex}
        isOpen={viewerIndex >= 0}
        onClose={() => setViewerIndex(-1)}
      />

      {/* Conversation action menu (header ···) */}
      {headerMenuPos && (
        <ConversationActionMenu
          conversationId={conversationId}
          isMuted={conversation?.isMuted ?? false}
          position={headerMenuPos}
          onClose={() => setHeaderMenuPos(null)}
          onMarkUnread={handleMarkUnread}
          onToggleMute={handleToggleMute}
          isArchived={conversation?.isArchived}
          onToggleArchive={handleToggleArchive}
          onLeave={handleLeave}
          onDelete={handleDelete}
        />
      )}
    </motion.div>
  );
}
